<template>
  <div class="full-height container q-mb-md note">
    <div ref="noteBox" class="note-box q-py-md">
      <div class="flex full-width column">
        <transition name="fade-loading">
          <div v-if="loading" class="flex justify-center">
            <q-spinner color="primary" size="lg" :thickness="3" />
          </div>
        </transition>

        <div v-for="note in notes" :key="note.id">
          <p class="text-center text-muted">
            {{ note.createdAt | formatDateTime }}
          </p>

          <div class="flex q-mb-md justify-start target items-end no-wrap">
            <span class="note-content">
              {{ note.content }}
              <q-tooltip v-if="note.creator">
                Created by: {{ note.creator.email }} {{ note.createdAt | formatDateTime }}
              </q-tooltip>
            </span>
            <q-btn flat padding="none" color="grey" icon="more_vert" size="sm" :disabled="saving">
              <q-menu>
                <q-list style="min-width: 100px">
                  <q-item v-close-popup clickable @click="edit(note)">
                    <q-item-section>Edit</q-item-section>
                  </q-item>
                  <q-item v-close-popup clickable @click="confirmDelete(note)">
                    <q-item-section>Delete</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
        <p v-if="notes.length > 0 && !saving" class="text-right text-muted q-mb-none status q-pr-md">
          <template>Saved</template>
        </p>
      </div>
    </div>
    <div class="flex items-end q-mb-md" style="height: 52px">
      <q-input
        v-model.trim="content"
        no-error-icon
        hide-bottom-space
        autogrow
        placeholder="Enter notes here"
        class="q-mr-md"
        style="flex-grow: 1"
        error-message="Maximum length reached!"
        :error="$v.content.$error"
        :disable="saving || loading"
        @keydown.enter.prevent="save"
      />

      <q-btn
        dense
        flat
        rounded
        icon-right="send"
        color="primary"
        :disabled="saving || loading || !content || $v.content.$error"
        @click="save"
      >
      </q-btn>
    </div>
    <q-dialog v-model="confirm" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <div class="text-h6">Confirmation</div>
        </q-card-section>
        <q-card-section class="q-pt-none"> Are you sure you want to delete this admin note? </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Back" color="primary" />
          <q-btn v-close-popup flat label="Delete" color="negative" @click="deleteNote" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import maxLength from 'vuelidate/lib/validators/maxLength';
import API from '@/apis';
import { getErrorMessage } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';

export default {
  name: 'Notes',

  mixins: [formatDateMixin],

  props: {
    userId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      content: '',
      notes: [],
      cursor: '',
      position: 0,
      selectedId: null,
      confirm: false,
    };
  },

  computed: {
    ...mapState(['user']),
  },

  watch: {
    notes() {
      this.scrollToLatestMessage();
    },
  },

  mounted() {
    this.getNotes();
    this.scrollToLatestMessage();
  },

  methods: {
    async getNotes() {
      this.loading = true;
      try {
        const { data } = await this.$axios.request(API.admin.getNotes(this.userId));
        this.notes = data;
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.loading = false;
      }
    },
    scrollToLatestMessage() {
      this.$nextTick(() => {
        const container = this.$refs.noteBox;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    async save() {
      if (!this.content) return;
      this.$v.$reset();
      this.$v.$touch();

      if (this.$v.$invalid) return;

      try {
        // Edit exist note
        this.saving = true;
        if (this.selectedId) {
          const { data: note } = await this.$axios.request(
            API.admin.updateNote({
              id: this.selectedId,
              content: this.content,
            })
          );

          const item = this.notes.find((n) => n.id === this.selectedId);
          if (item) {
            item.content = note.content;
          }
        } else {
          const { data: note } = await this.$axios.request(
            API.admin.createNote({
              userId: this.userId,
              content: this.content,
            })
          );

          this.notes.push(note);
        }

        this.content = '';
        this.selectedId = null;
        this.scrollToLatestMessage();
      } catch (e) {
        this.$q.notify({ message: getErrorMessage(e), type: 'negative' });
      } finally {
        this.saving = false;
      }
    },
    edit(note) {
      this.selectedId = note.id;
      this.content = note.content;
    },
    confirmDelete(note) {
      this.selectedId = note.id;
      this.confirm = true;
    },
    async deleteNote() {
      if (!this.selectedId) {
        return;
      }
      try {
        this.saving = true;
        await this.$axios.request(API.admin.deleteNote(this.selectedId));
        this.$q.notify({
          message: 'The note has been deleted successfully',
          type: 'positive',
        });

        this.notes = this.notes.filter((n) => n.id !== this.selectedId);
        this.selectedId = null;
      } catch (e) {
        this.$q.notify({ message: getErrorMessage(e), type: 'negative' });
      } finally {
        this.saving = false;
      }
    },
  },

  validations: {
    content: {
      maxLength: maxLength(1000),
    },
  },
};
</script>

<style lang="scss" scoped>
$container-height: 600px;
$info-height: 100px;

.note {
  display: flex;
  flex-flow: column nowrap;
  background-color: #dde6e9;
  max-height: 200px;
}

.note-box {
  position: relative;
  flex-grow: 1;
  overflow: auto;
  background-color: #dde6e9;
  border-bottom: 1px solid #cfcfcf;
}

.note-content {
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  white-space: pre-line;
}

.send-file-icon {
  height: 2rem;
  width: 2rem;
  cursor: pointer;
}

.status {
  margin-top: -0.75rem;
  font-size: 12px;
}

.fade-loading-enter-active,
.fade-loading-leave-active {
  transition: opacity 200ms;
}
.fade-loading-enter, .fade-loading-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
